import {
    takeEvery,
    put,
    call,
    delay,
    cancelled,
    take,
    race,
} from 'redux-saga/effects';
import { SendApiRequest } from '../../api';
import { apiRoutes } from '../../api/routes';
import { showToastAction } from '../ui';
import {
    createReportAction,
    fetchReportsAction,
    pollReportStatusAction,
    stopPollingAction,
    checkExistingReportAction,
    deleteReportAction,
    setLoading,
    setCreating,
    reportsFetched,
    reportCreated,
    reportStatusUpdated,
    reportDeleted,
    setExistingReport,
    setCheckingExisting,
    setError,
} from '../reports';

function* createReport(action) {
    try {
        yield put(setCreating(true));
        const result = yield call(SendApiRequest, {
            url: apiRoutes.reports.createTransaction,
            method: 'POST',
            body: action.payload,
            baseURL: apiRoutes.reports.workerBaseUrl,
        });
        yield put(reportCreated(result.reportJob));
        yield put(
            showToastAction({
                message: 'Report queued successfully',
                type: 'success',
            }),
        );
        // Start polling
        yield put(pollReportStatusAction(result.reportJob._id));
    } catch (e) {
        yield put(setError(e.message));
        yield put(
            showToastAction({
                message: e.message || 'Failed to create report',
                type: 'danger',
            }),
        );
    }
}

function* fetchReports(action) {
    try {
        yield put(setLoading(true));
        const page = action.payload?.page || 0;
        const result = yield call(SendApiRequest, {
            url: apiRoutes.reports.listTransactions,
            method: 'GET',
            queryParams: { page, limit: 20 },
        });
        yield put(
            reportsFetched({ reports: result.reports, total: result.total }),
        );
    } catch (e) {
        yield put(setError(e.message));
    }
}

function* pollReportStatus(action) {
    const reportId = action.payload;
    try {
        while (true) {
            const result = yield call(SendApiRequest, {
                url: apiRoutes.reports.transactionStatus(reportId),
                method: 'GET',
            });
            yield put(reportStatusUpdated(result.report));

            if (
                result.report.status === 'completed' ||
                result.report.status === 'failed'
            ) {
                if (result.report.status === 'completed') {
                    yield put(
                        showToastAction({
                            message:
                                'Report completed! Check your email for the download link.',
                            type: 'success',
                        }),
                    );
                } else {
                    yield put(
                        showToastAction({
                            message:
                                'Report failed: ' +
                                (result.report.result?.errorMessage ||
                                    'Unknown error'),
                            type: 'danger',
                        }),
                    );
                }
                break;
            }

            // Adaptive polling: start at 8s, reduce as ETA shrinks
            const etaMs = result.report.progress?.etaMs || 0;
            let pollInterval = 8000;
            if (etaMs > 0 && etaMs < 30000) pollInterval = 4000;
            if (etaMs > 0 && etaMs < 10000) pollInterval = 2000;
            yield delay(pollInterval);
        }
    } finally {
        if (yield cancelled()) {
            // Polling was cancelled
        }
    }
}

function* pollReportStatusWatcher(action) {
    yield race({
        poll: call(pollReportStatus, action),
        cancel: take(stopPollingAction.type),
    });
}

function* checkExistingReport() {
    try {
        yield put(setCheckingExisting(true));
        const result = yield call(SendApiRequest, {
            url: apiRoutes.reports.checkExisting,
            method: 'GET',
        });
        yield put(setExistingReport(result.existing));
    } catch (e) {
        yield put(setCheckingExisting(false));
    }
}

function* deleteReport(action) {
    const reportId = action.payload;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.reports.deleteTransaction(reportId),
            method: 'DELETE',
        });
        yield put(reportDeleted(reportId));
        yield put(
            showToastAction({
                message: 'Report deleted',
                type: 'success',
            }),
        );
    } catch (e) {
        yield put(
            showToastAction({
                message: e.message || 'Failed to delete report',
                type: 'danger',
            }),
        );
    }
}

export const reportsSagas = [
    takeEvery(createReportAction.type, createReport),
    takeEvery(fetchReportsAction.type, fetchReports),
    takeEvery(pollReportStatusAction.type, pollReportStatusWatcher),
    takeEvery(checkExistingReportAction.type, checkExistingReport),
    takeEvery(deleteReportAction.type, deleteReport),
];
